import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, ProgressBar, Table, Spinner, Button } from 'react-bootstrap';
import { apiFetch } from '../../../utils/api.js';

export function Stat() {
  const [stats, setStats] = useState({ cpu: 0, ram: 0, disk: 0 });
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const [sRes, lRes] = await Promise.all([
        apiFetch('/api/admin/stats'),
        apiFetch('/api/admin/leads')
      ]);
      if (sRes.ok) setStats(await sRes.json());
      if (lRes.ok) setLeads(await lRes.json());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
      let timer;
      const poll = async () => {
        await fetchData(); // Ждем завершения запроса
        timer = setTimeout(poll, 5000); // Только ПОСЛЕ ответа планируем следующий через 5 сек
      };

      poll(); // Запуск
      return () => clearTimeout(timer); // Очистка при уходе со страницы
    }, []);

  const handleToggleLead = async (id) => {
    const res = await apiFetch(`/api/admin/leads/${id}/toggle`, { method: 'PATCH' });
    if (res.ok) fetchData(); // Быстро обновляем список
  };

  const isOverloaded = stats.cpu > 90 || stats.ram > 90;

  const MetricCard = ({ title, value, icon, color, progress }) => (
    <Card className="border-0 shadow-sm h-100 p-3">
      <div className="d-flex align-items-center mb-3">
        <div className={`metric-icon-box bg-${color} bg-opacity-10 me-3`}>
          <i className={`bi ${icon} text-${color} fs-3`}></i>
        </div>
        <div>
          <div className="text-muted small fw-bold text-uppercase" style={{ fontSize: '0.65rem' }}>{title}</div>
          <h4 className="fw-bold m-0">{value}</h4>
        </div>
      </div>
      <ProgressBar variant={color} now={progress} style={{ height: '4px' }} className="rounded-pill" />
    </Card>
  );

  return (
    <Container>
      <h2 className="fw-bold mb-4">Мониторинг системы</h2>
      
      <Row className="g-4 mb-5">
        <Col md={3}><MetricCard title="Процессор" value={`${stats.cpu}%`} icon="bi-cpu-fill" color="danger" progress={stats.cpu} /></Col>
        <Col md={3}><MetricCard title="Оперативная память" value={`${stats.ram}%`} icon="bi-memory" color="warning" progress={stats.ram} /></Col>
        <Col md={3}><MetricCard title="Дисковое пространство" value={`${stats.disk}%`} icon="bi-hdd-network-fill" color="primary" progress={stats.disk} /></Col>
        <Col md={3}><MetricCard title="Всего заявок" value={leads.length} icon="bi-envelope-paper-heart-fill" color="success" progress={100} /></Col>
      </Row>

      <h4 className="fw-bold mb-3 d-flex align-items-center">
        Последние обращения 
        <span className="badge bg-primary ms-3 fs-6 rounded-pill">
          {leads.filter(l => !l.is_processed).length} новые
        </span>
      </h4>
      
      <Card className="border-0 shadow-sm mb-5 overflow-hidden">
        <Card.Body className="p-0">
          <Table hover responsive className="m-0 align-middle">
            <thead className="bg-light">
              <tr>
                <th className="px-4 py-3 border-0 small text-uppercase text-muted">Дата</th>
                <th className="py-3 border-0 small text-uppercase text-muted">Клиент</th>
                <th className="py-3 border-0 small text-uppercase text-muted">Телефон</th>
                <th className="px-4 py-3 border-0 text-end small text-uppercase text-muted">Статус</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="4" className="text-center py-5"><Spinner animation="border" size="sm" variant="primary" /></td></tr>
              ) : leads.map(lead => (
                <tr key={lead.id} className={lead.is_processed ? 'lead-row-processed' : ''}>
                  <td className="px-4 py-3 text-muted small">
                    {new Date(lead.created_at).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                  </td>
                  <td className="fw-bold">{lead.name}</td>
                  <td>
                    <a href={`tel:${lead.phone}`} className="text-decoration-none text-dark fw-medium">
                      <i className="bi bi-telephone-fill me-2 text-accent small"></i>{lead.phone}
                    </a>
                  </td>
                  <td className="px-4 text-end">
                    <Button 
                      variant={lead.is_processed ? "light" : "outline-success"} 
                      size="sm" 
                      className="rounded-pill px-3"
                      onClick={() => handleToggleLead(lead.id)}
                    >
                      {lead.is_processed ? (
                        <><i className="bi bi-check-all me-1"></i> Отработано</>
                      ) : (
                        <><i className="bi bi-check2"></i> Завершить</>
                      )}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Card className={`border-0 shadow-sm p-4 ${isOverloaded ? 'bg-danger' : 'bg-dark'} text-white`}>
        <h6 className="m-0 d-flex align-items-center">
          <span className={`p-1 rounded-circle me-3 ${isOverloaded ? 'bg-warning' : 'bg-success'}`} style={{ width: '10px', height: '10px', display: 'inline-block' }}></span>
          {isOverloaded ? 'Внимание: Сервер перегружен. Рекомендуется проверить запущенные процессы.' : 'Система работает стабильно. Проблем не обнаружено.'}
        </h6>
      </Card>
    </Container>
  );
}
